/**
 * PlayerPreviewDrawerContainer — data + actions for the player preview drawer
 * (SE-5494).
 *
 * Opens off the SAME `selectedPlayerIdVar` the panel it replaces used, so the
 * `handlePlayerClick` seam in `MainContainer` is untouched: desktop still calls
 * `setSelectedPlayer(playerId)`, and the small-screen fork still routes to the
 * full player page. The mobile fork stays gated on SCREEN SIZE, never device
 * type (`.claude/rules/screen-size-not-device-type.md`).
 *
 * ## AC4 — the stage change is not a separate code path
 *
 * `onMoveToStage` is threaded in from the screen and is literally
 * `useKanbanBoard().movePlayerToPhase` — the same function `handlePlayerDrop`
 * calls for a drag. That function resolves the destination stage's gate through
 * `resolveStageMoveGate` and awaits the ONE `requestStageMoveDecision` resolver
 * that `useKanbanBoard` injected into `useKanbanCardOperations`, whose prompt
 * component is already mounted once at the screen root
 * (`MainContainer.tsx`, `{stageMovePromptComponent}`).
 *
 * This container therefore does NOT call `useStageMovePrompt`. A second
 * instance would mount a second modal and settle a different promise — the
 * "separate code path" AC4 forbids — so the prompt is deliberately absent here.
 *
 * ## Queries
 *
 * - `usePlayerDetailQuery` — the same document the player profile uses, so a
 *   later navigation to the profile is a warm cache read rather than a refetch.
 * - `useNotesListNotesByPlayerIdQuery` — the pinned latest note (AC1/AC2).
 * - `usePlayerDetailListBasicCompetitionStatsQuery` — the only per-player
 *   source of minutes / goals / assists in this schema.
 * - `usePipelineActivityTrail` (SE-5504) — this CARD's audit trail, scoped
 *   server-side by `kanbanCardId`. Board-scoped-then-narrowed was not an
 *   option: the ticket forbids client-side filtering of the log.
 * - `useKanbanCardTags` (SE-6920) — this CARD's tags, read fresh so a tag a
 *   server-side automation applied is not invisible until a reload. The `tags`
 *   prop (board-derived, `cache-first`) stays as the fallback.
 *
 * All are skipped while the drawer is closed, so a board with 200 cards issues
 * nothing until a card is actually clicked.
 * @remarks Container component — owns queries, actions, and the breakpoint.
 * @module features/player-kanban/components/organisms/PlayerPreviewDrawer
 */

import { useCallback, useMemo, useRef, useState } from "react";

import TagSelector from "@/features/player-kanban/components/organisms/TagSelector";

import type { TCardMoveMethod } from "@/features/analytics";
import { buildSeasonIdToYearMap } from "@/features/player-detail/utils/performanceAnalyticsUtils";
import {
  CurrencyCode,
  useNotesListNotesByPlayerIdQuery,
  usePlayerDetailListBasicCompetitionStatsQuery,
} from "@/generated/graphql";
import { useAppLocale } from "@/i18n";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import useMobileScreen from "@/hooks/useMobileScreen";

import { useKanbanCardTags } from "../../../hooks/useKanbanCardTags";
import { usePipelineActivityTrail } from "../../../hooks/usePipelineActivityTrail";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

import { formatActivityRelativeTimestamp } from "../../../utils/pipelineActivityTimeline";
import { selectPinnedNote } from "../../../utils/pinnedNote";
import {
  buildKeyInfoRows,
  buildAttributesRows,
  buildRatingsRows,
  buildReportRows,
  buildSeasonFormRows,
  formatPinnedNoteAge,
  formatPreviewDate,
  isPinnedNoteNew,
  selectAvailability,
  selectClubName,
  selectDisplayName,
  selectHeroMeta,
  selectLatestVerdict,
  selectNoteCount,
  selectPositionLabel,
  selectReportCount,
} from "../../../utils/playerPreviewModel";
import PlayerPreviewDrawerView, {
  type PlayerPreviewPinnedNote,
  type PlayerPreviewTag,
} from "./PlayerPreviewDrawerView";

import type { PlayerPreviewStageOption } from "../../molecules/PlayerPreviewStagePicker";

/** How many notes to fetch when resolving the single pinned one. */
const PINNED_NOTE_FETCH_SIZE = 10;

/** Props for {@link PlayerPreviewDrawerContainer}. */
export interface PlayerPreviewDrawerContainerProps {
  /** The player whose preview is open; `null`/absent closes the drawer. */
  readonly selectedPlayerId?: string | null;
  /** Close handler — clears the selection. */
  readonly onClose: () => void;
  /** Board stages, in board order. */
  readonly stages: readonly PlayerPreviewStageOption[];
  /** The selected player's current stage id. */
  readonly currentStageId?: string;
  /**
   * Tags on the selected player's card, as the BOARD read has them. SE-6920
   * treats these as the fallback: the card-scoped read overrides them once it
   * answers.
   */
  readonly tags: readonly PlayerPreviewTag[];
  /** Whole days the card has spent in its current stage (SE-5497). */
  readonly daysInStage?: number | null;
  /** The active board — scopes the audit trail (SE-5504). */
  readonly kanbanBoardId?: string | null;
  /**
   * The selected player's KANBAN CARD id — narrows the audit trail to this one
   * card SERVER-SIDE (SE-5504). Absent while the board resolves, which simply
   * leaves the trail unfetched.
   */
  readonly kanbanCardId?: string | null;
  /** Whether the viewer may move this card. */
  readonly canEdit: boolean;
  /** Whether the active board is a PIPELINE board (drives the remove label). */
  readonly isPipelineBoard: boolean;
  /** The org's currency, for the market-value row. */
  readonly currencyCode?: CurrencyCode;
  /**
   * The board's shared stage-move service — `useKanbanBoard().movePlayerToPhase`.
   * See the AC4 note in the module docblock: this is the drag path's function,
   * not a drawer-local reimplementation.
   */
  readonly onMoveToStage: (
    playerId: string,
    fromPhaseId: string,
    toPhaseId: string,
    insertionIndex?: number,
    method?: TCardMoveMethod
  ) => Promise<void>;
  /** Opens the full player profile. */
  readonly onOpenProfile: (playerId: string) => void;
  /** Opens the player's notes list. */
  readonly onViewAllNotes: (playerId: string) => void;
  /**
   * SE-5500 — performs the removal of THIS player's card from the board,
   * recording the confirmed reason on the audit trail. Wired to the board's
   * `removePlayerFromBoard` service; MUST reject when the removal failed so
   * the confirmation dialog can stay open with the typed reason intact.
   */
  readonly onRemove: (playerId: string, rationale: string) => Promise<void>;
}

/**
 * Container for the player preview drawer.
 * @param props - {@link PlayerPreviewDrawerContainerProps}
 * @param props.selectedPlayerId - The player whose preview is open
 * @param props.onClose - Clears the selection
 * @param props.stages - Board stages, in board order
 * @param props.currentStageId - The selected player's current stage id
 * @param props.tags - Board-derived tags; the SE-6920 fallback
 * @param props.daysInStage - Whole days the card has spent in its stage
 * @param props.kanbanBoardId - The active board, scoping the audit trail
 * @param props.kanbanCardId - The card whose audit trail the drawer shows
 * @param props.canEdit - Whether the viewer may move this card
 * @param props.isPipelineBoard - Whether the board is a PIPELINE board
 * @param props.currencyCode - The org's currency, for the market-value row
 * @param props.onMoveToStage - The board's SHARED stage-move service (AC4)
 * @param props.onOpenProfile - Opens the full player profile
 * @param props.onViewAllNotes - Opens the player's notes list
 * @param props.onRemove - Remove action (render-only until SE-5500)
 * @returns The wired {@link PlayerPreviewDrawerView}.
 */
const PlayerPreviewDrawerContainer = ({
  selectedPlayerId,
  onClose,
  stages,
  currentStageId,
  tags,
  daysInStage,
  kanbanBoardId,
  kanbanCardId,
  canEdit,
  isPipelineBoard,
  currencyCode = CurrencyCode.Eur,
  onMoveToStage,
  onOpenProfile,
  onViewAllNotes,
  onRemove,
}: PlayerPreviewDrawerContainerProps) => {
  const isSmallScreen = useMobileScreen();
  const locale = useAppLocale();
  const isOpen = Boolean(selectedPlayerId);
  const playerId = selectedPlayerId ?? "";

  const {
    data: playerData,
    loading: playerLoading,
    error: playerError,
  } = usePlayerDetailQuery({
    variables: { id: playerId },
    skip: !isOpen,
  });

  const { data: notesData, loading: notesLoading } =
    useNotesListNotesByPlayerIdQuery({
      variables: { playerId, first: PINNED_NOTE_FETCH_SIZE },
      skip: !isOpen,
    });

  const { data: basicStatsData } =
    usePlayerDetailListBasicCompetitionStatsQuery({
      variables: { playerId },
      skip: !isOpen,
    });

  /**
   * SE-5504 AC1/AC2 — this card's audit trail. Scoped by `kanbanCardId` so the
   * server does the narrowing; skipped while the drawer is closed OR before the
   * board has resolved a card id, so an open drawer on a not-yet-loaded board
   * shows the empty state rather than another card's history.
   */
  const activity = usePipelineActivityTrail({
    kanbanBoardId,
    kanbanCardId,
    skip: !isOpen || !kanbanCardId,
  });

  /**
   * SE-6920 — the tags the TAGS section actually renders.
   *
   * The `tags` PROP is the board read's answer, and the board read is
   * `cache-first` with nothing invalidating it when the server changes a tag
   * (an automation rule, SE-6860). So the prop is kept as the fallback and the
   * card-scoped read decides: fresh tags when the server has answered, the
   * board's otherwise. `skip` carries the surface half of the gate only — the
   * hook refuses to read without a card id and a board on its own, the same
   * data-readiness rule the audit trail above follows.
   */
  const resolvedTags = useKanbanCardTags({
    kanbanCardId,
    kanbanBoardId,
    boardTags: tags,
    skip: !isOpen,
  });

  const player = playerData?.getPlayer;

  /**
   * AC1/AC2 — the SINGLE most recent note, with no recency window.
   * `selectPinnedNote` sorts defensively rather than trusting `edges[0]`,
   * because the `NotesListNotesByPlayerId` document encodes no `orderBy`.
   */
  const pinnedNote = useMemo((): PlayerPreviewPinnedNote | undefined => {
    const notes = (notesData?.listNotesByPlayerId?.edges ?? []).map(
      edge => edge.node
    );
    const newest = selectPinnedNote(notes);
    if (!newest) return undefined;

    return {
      id: newest.id,
      body: newest.body?.trim() || newest.title?.trim() || "",
      createdAt: formatPreviewDate(newest.createdAt) ?? "",
      // The author line reads relatively ("2 days ago"), which is the same
      // vocabulary the pipeline activity log speaks — one phrasing for "when"
      // across the two surfaces a card's history is read on.
      relativeCreatedAt: formatActivityRelativeTimestamp(newest.createdAt),
      isNew: isPinnedNoteNew(newest.createdAt),
      ageLabel: formatPinnedNoteAge(newest.createdAt),
      /*
       * `Username` is the Cognito `sub` in this pool — a UUID, not a name — so
       * rendering it printed "04787478-f081-… · Apr 30" where the player page
       * showed "test49 49". The `NotesNote` fragment already selects the
       * attribute lists the name lives in, so this needed decoding, not a wider
       * query.
       *
       * Resolved through the SAME helper and the SAME `Attributes ??
       * UserAttributes` coalescing the detail page's `NotesSectionContainer`
       * uses on this identical fragment, so the two surfaces cannot render one
       * note's author two ways. The fallback is the board's own idiom for an
       * unnameable person (`ACTOR_TYPE_LABEL[User]` in `pipelineActivityRows`)
       * rather than the helper's default "Unknown" — and never a raw id.
       */
      authorName: getCognitoDisplayName(
        newest.user?.cognitoUser?.Attributes ??
          newest.user?.cognitoUser?.UserAttributes,
        "Team member"
      ),
    };
  }, [notesData?.listNotesByPlayerId?.edges]);

  const basicStats = useMemo(
    () => basicStatsData?.listBasicCompetitionStats ?? [],
    [basicStatsData?.listBasicCompetitionStats]
  );

  const seasonIdToYear = useMemo(
    () => buildSeasonIdToYearMap(player, basicStats),
    [player, basicStats]
  );

  const keyInfoRows = useMemo(
    () => buildKeyInfoRows(player ?? {}, currencyCode),
    [player, currencyCode]
  );
  const ratingsRows = useMemo(() => buildRatingsRows(player ?? {}), [player]);
  const attributesRows = useMemo(
    () => buildAttributesRows(player ?? {}),
    [player]
  );
  const seasonFormRows = useMemo(
    () =>
      buildSeasonFormRows(
        player ?? {},
        daysInStage,
        basicStats,
        seasonIdToYear,
        // SE-7966: passed EXPLICITLY rather than relying on the parameter's
        // default. The default exists only because TypeScript forbids a
        // required parameter after optional ones, and a default that silently
        // yields en_US is exactly the failure this ticket removes — so
        // production call sites state the locale.
        locale
      ),
    [player, daysInStage, basicStats, seasonIdToYear, locale]
  );
  const reports = useMemo(() => buildReportRows(player ?? {}), [player]);
  const verdict = useMemo(() => selectLatestVerdict(player ?? {}), [player]);

  /**
   * Whether a stage move is in flight. Drives the picker's disabled state.
   * @remarks
   * Mirrored into a ref because `setState` is asynchronous: two presses landing
   * in the same tick would both read a stale `false` and fire duplicate moves.
   * The ref is the correctness guard, the state is what re-renders the picker.
   */
  const [isMoving, setIsMoving] = useState(false);
  const isMovingRef = useRef(false);

  /**
   * AC4 — hand the selection straight to the board's shared move service.
   * Everything that makes a move correct (the protected/terminal gate, the
   * reason prompt, the optimistic column pin, the terminal-close routing) lives
   * inside `onMoveToStage`; nothing is re-decided here.
   */
  const handleSelectStage = useCallback(
    (toPhaseId: string) => {
      if (!selectedPlayerId || !currentStageId) return;
      if (toPhaseId === currentStageId) return;
      // `movePlayerToPhase` does not de-duplicate overlapping calls itself.
      if (isMovingRef.current) return;

      isMovingRef.current = true;
      setIsMoving(true);

      // SE-5508: the 5th argument tags this as the stage-PICKER path so
      // `card_moved.method` distinguishes it from drag. It is the only reason
      // this call differs from the drag path's — everything that makes a move
      // correct still lives inside the shared service. `insertionIndex` is
      // `undefined` because the picker expresses no ordering intent; the
      // service appends.
      void onMoveToStage(
        selectedPlayerId,
        currentStageId,
        toPhaseId,
        undefined,
        "stage_picker"
      )
        .catch(() => {
          // Surfaced by the move service's own error toast; swallowing here
          // keeps a rejected move from becoming an unhandled rejection.
        })
        .finally(() => {
          isMovingRef.current = false;
          setIsMoving(false);
        });
    },
    [selectedPlayerId, currentStageId, onMoveToStage]
  );

  /**
   * One stable press handler per stage. Built here rather than in the View
   * because a View may hold no hooks, and inline JSX closures are lint-banned.
   */
  const stageHandlers = useMemo(
    () =>
      Object.fromEntries(
        stages.map(stage => [stage.id, () => handleSelectStage(stage.id)])
      ) as Record<string, () => void>,
    [stages, handleSelectStage]
  );

  /**
   * Both footer/section links NAVIGATE AWAY, so both close the drawer on the
   * way out.
   *
   * Without this the drawer stays open behind the player page: the board screen
   * is not unmounted by the push, and the selection it holds is what keeps the
   * drawer mounted — so coming back (breadcrumb, browser Back) lands on a board
   * with a drawer still hanging over it, describing a player the user has since
   * left. Closing is done AFTER handing the navigation off so the id is still
   * resolved when the callback reads it.
   */
  const handleOpenProfile = useCallback(() => {
    if (!selectedPlayerId) return;
    onOpenProfile(selectedPlayerId);
    onClose();
  }, [selectedPlayerId, onOpenProfile, onClose]);

  const handleViewAllNotes = useCallback(() => {
    if (!selectedPlayerId) return;
    onViewAllNotes(selectedPlayerId);
    onClose();
  }, [selectedPlayerId, onViewAllNotes, onClose]);

  /**
   * SE-5500 — the footer button OPENS the confirmation; it never removes.
   * Every removal goes through the dialog, which is where the required reason
   * is collected and the double-submit guard lives.
   */
  /**
   * SE-7019: the state records WHICH player the confirmation was opened for,
   * not merely THAT it is open. A boolean outlives the selection it was set
   * under — reach another card behind the drawer, or close the drawer and
   * reopen it on someone else — and the still-true flag leaves the dialog on
   * screen wired to the NEW `selectedPlayerId`, so confirming it DELETES that
   * player's card under a reason written about the first one.
   */
  const [removeDialogPlayerId, setRemoveDialogPlayerId] = useState<
    string | null
  >(null);

  /*
   * Adjusting state during render (React's documented alternative to a reset
   * effect, and the only option here — `react-hooks/set-state-in-effect` is an
   * error in this repo). React re-runs this component before committing, so
   * the dialog is never even momentarily mounted against the new player; an
   * effect would run AFTER that commit, leaving a confirmable dialog pointed
   * at the wrong card for a frame.
   *
   * RELEASING the id rather than only comparing it against the selection is
   * what stops a return to the original player from resurrecting a dialog they
   * had already left behind.
   */
  if (
    removeDialogPlayerId !== null &&
    removeDialogPlayerId !== selectedPlayerId
  ) {
    setRemoveDialogPlayerId(null);
  }

  const handleRemove = useCallback(() => {
    if (selectedPlayerId) setRemoveDialogPlayerId(selectedPlayerId);
  }, [selectedPlayerId]);

  const handleCancelRemove = useCallback(() => {
    setRemoveDialogPlayerId(null);
  }, []);

  const handleConfirmRemove = useCallback(
    (rationale: string) => {
      // Dispatched against the id the dialog was OPENED for, so a removal can
      // only ever land on the player the reason was written about.
      if (!removeDialogPlayerId) return Promise.resolve();
      return onRemove(removeDialogPlayerId, rationale);
    },
    [removeDialogPlayerId, onRemove]
  );

  /**
   * The card is gone, so a drawer describing it is stale — close the dialog
   * and the drawer. The board itself reconciles through the removal's
   * `ListKanbanCards` refetch, not through anything this component holds.
   */
  const handleRemoved = useCallback(() => {
    setRemoveDialogPlayerId(null);
    onClose();
  }, [onClose]);

  // SE-6859: the tags section's "+" opens the board's existing tag selector —
  // the same modal (picker, creation, persistence) the Table's Tags column
  // reaches — so the drawer adds an entry point, not a second editing surface.
  // Card chips update immediately because the selector's mutations write the
  // same board cache this drawer's `tags` prop is derived from.
  //
  // SE-6997: the state records WHICH player the selector was opened for, not
  // merely THAT it is open. A boolean outlives the selection it was set under —
  // click another card behind the drawer, or close the drawer and reopen it on
  // someone else, and the still-true flag re-mounts the selector against the
  // NEW `selectedPlayerId`. The modal looks unchanged, so a tag picked in it is
  // written to a player the user never opened it for.
  const [tagSelectorPlayerId, setTagSelectorPlayerId] = useState<string | null>(
    null
  );

  // Adjusting state during render (React's documented alternative to a reset
  // effect, and the only option here — `react-hooks/set-state-in-effect` is an
  // error in this repo). React re-runs this component before committing, so the
  // selector is never even momentarily mounted against the new player.
  //
  // RELEASING the id rather than only comparing it against the selection is
  // what stops a return to the original player from resurrecting a modal they
  // left behind.
  if (
    tagSelectorPlayerId !== null &&
    tagSelectorPlayerId !== selectedPlayerId
  ) {
    setTagSelectorPlayerId(null);
  }

  const handleAddTag = useCallback(() => {
    if (selectedPlayerId) setTagSelectorPlayerId(selectedPlayerId);
  }, [selectedPlayerId]);
  const handleCloseTagSelector = useCallback(
    () => setTagSelectorPlayerId(null),
    []
  );
  // Tag editing needs a board to mutate against, a card to attach to, and the
  // right to do it — the same gate the Table applies.
  const canAddTags =
    canEdit && Boolean(kanbanBoardId) && Boolean(selectedPlayerId);

  return (
    <>
      <PlayerPreviewDrawerView
        isOpen={isOpen}
        onClose={onClose}
        isSmallScreen={isSmallScreen}
        isLoading={playerLoading || notesLoading}
        errorMessage={playerError ? "Could not load this player." : undefined}
        playerName={selectDisplayName(player ?? {})}
        clubName={selectClubName(player ?? {})}
        positionLabel={selectPositionLabel(player ?? {})}
        heroMeta={selectHeroMeta(player ?? {})}
        availability={selectAvailability(player ?? {})}
        gpr={
          player?.bioData?.gpr != null
            ? String(Math.round(player.bioData.gpr * 10) / 10)
            : undefined
        }
        pinnedNote={pinnedNote}
        noteCount={selectNoteCount(player ?? {})}
        onViewAllNotes={handleViewAllNotes}
        stages={stages}
        currentStageId={currentStageId}
        canEdit={canEdit}
        isMoving={isMoving}
        stageHandlers={stageHandlers}
        keyInfoRows={keyInfoRows}
        ratingsRows={ratingsRows}
        attributesRows={attributesRows}
        seasonFormRows={seasonFormRows}
        reports={reports}
        reportCount={selectReportCount(player ?? {})}
        verdict={verdict}
        tags={resolvedTags}
        activityRows={activity.rows}
        activityLoading={activity.loading}
        activityIsError={activity.isError}
        activityHasMore={activity.hasMore}
        activityIsLoadingMore={activity.isLoadingMore}
        onLoadMoreActivity={activity.loadMore}
        onOpenProfile={handleOpenProfile}
        // AC1 — removal is an EDIT-access action, so viewers without it get no
        // remove affordance at all (product decision 2026-07-16: edit access,
        // not Owner-only and not Manager+-only).
        canRemove={canEdit}
        removeLabel={
          isPipelineBoard ? "Remove from pipeline" : "Remove from board"
        }
        onRemove={handleRemove}
        isPipelineBoard={isPipelineBoard}
        isRemoveDialogOpen={removeDialogPlayerId !== null}
        onCancelRemove={handleCancelRemove}
        onConfirmRemove={handleConfirmRemove}
        onRemoved={handleRemoved}
        onAddTag={canAddTags ? handleAddTag : undefined}
      />
      {/* SE-6859: mounted beside the drawer exactly as PipelineTableContainer
          mounts it beside the table — same component, same mutations. */}
      {/* SE-6997: mounted against the id the selector was OPENED for, so the
          modal can only ever write to that player. */}
      {tagSelectorPlayerId && kanbanBoardId ? (
        <TagSelector
          isOpen
          onClose={handleCloseTagSelector}
          playerId={tagSelectorPlayerId}
          boardId={kanbanBoardId}
        />
      ) : null}
    </>
  );
};

export default PlayerPreviewDrawerContainer;
